"use client";

import React, { type ReactElement, useRef } from "react";
import { useAtomValue } from "jotai";
import { mapTileAtom } from "@/store/sites";
import { RMap } from "maplibre-react-components";
import { LngLatLike, Map, MapLibreEvent, LngLatBounds } from "maplibre-gl";
import { useSites } from "@/hooks/sites";
import { Site, ThemeTypeEnum } from "@/types";
import { SitesMapLayerTheme } from "@/components";
import { useMapLibre } from "@/hooks";

interface HomeFeaturedSitesMapProps {
  sites: Site[];
}

function HomeFeaturedSitesMap({
  sites,
}: Readonly<HomeFeaturedSitesMapProps>): ReactElement | null {
  const { filterSitesByThemes } = useSites(sites);
  const { sprite } = useMapLibre();

  const filteredSites = filterSitesByThemes();
  const mapRef = useRef<Map>(null);
  const center: LngLatLike = [3.8593521385608796, 43.62179360643451];
  const tile = useAtomValue(mapTileAtom);

  if (!sites) return null;

  const bounds = new LngLatBounds();
  sites.forEach((site) => bounds.extend([site.longitude, site.latitude]));

  const handleLoad = (e: MapLibreEvent) => {
    const map = e.target;
    map.setSprite(sprite);

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: 100,
        duration: 0,
      });
    }

    map.dragPan.disable();
    map.scrollZoom.disable();
  };

  return (
    <RMap
      id="map"
      initialCanvasContextAttributes={{ antialias: true }}
      initialCenter={center}
      initialZoom={5}
      mapStyle={tile.url}
      ref={mapRef}
      initialAttributionControl={false}
      onLoad={handleLoad}
      className="hover:cursor-default"
      initialInteractive={false}
    >
      <SitesMapLayerTheme
        sites={filteredSites.earth}
        theme={ThemeTypeEnum.EARTH}
        interactive={false}
      />
      <SitesMapLayerTheme
        sites={filteredSites.water}
        theme={ThemeTypeEnum.WATER}
        interactive={false}
      />
      <SitesMapLayerTheme
        sites={filteredSites.biodiversity}
        theme={ThemeTypeEnum.BIODIVERSITY}
        interactive={false}
      />
      <SitesMapLayerTheme
        sites={filteredSites.universe}
        theme={ThemeTypeEnum.UNIVERSE}
        interactive={false}
      />
    </RMap>
  );
}

export { HomeFeaturedSitesMap };
